// @givmo/app-charity — router factory + type registration.
//
// `createCharityRouter` builds a router over the code-based route tree with a PLACEHOLDER auth context
// (the live `AuthState` is injected via `<RouterProvider context={{ auth }} />` in App). A `history`
// override is accepted so component tests can drive an in-memory router (`createMemoryHistory`).
//
// The module-level `router` instance exists only to feed the `Register` declaration (the typed
// `Link`/`redirect` surface); App passes its own instance to `RouterProvider`. (Mirrors admin.)

import { createRouter, type RouterHistory } from '@tanstack/react-router';
import { routeTree } from './routes';
import { NotFound } from '../components/not-found';
import { AppError } from '../components/app-error';

export interface CreateCharityRouterOptions {
  /** Override the history (tests pass `createMemoryHistory`). Defaults to the browser history. */
  history?: RouterHistory;
}

export function createCharityRouter(options: CreateCharityRouterOptions = {}) {
  return createRouter({
    routeTree,
    // Placeholder — filled per-render by `<RouterProvider context={{ auth }} />`.
    context: { auth: undefined! },
    defaultPreload: 'intent',
    defaultNotFoundComponent: NotFound,
    defaultErrorComponent: AppError,
    ...(options.history ? { history: options.history } : {}),
    scrollRestoration: true,
  });
}

/** The typed router instance used for the `Register` declaration below. */
export const router = createCharityRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
